import { Component, Input, OnInit } from '@angular/core';
import {
  PastWorkshopModel,
  WorkshopHoldingTimeModel,
} from '../../../features/held-workshop/models/past-workshop.model';
import { ProfileService } from '../../../features/profile/services/profile.service';
import { NotifierService } from 'angular-notifier';
import { PersianTitlesService } from '../../../core/services/persian-titles.service';
import { CommonModule } from '@angular/common';
import { NgIconComponent } from '@ng-icons/core';
import {
  SharedWorkshopCardComponent,
  SharedWorkshopCardAction,
  SharedWorkshopCardRow,
} from 'src/app/shared/components/shared-workshop-card/shared-workshop-card.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DxPopupModule } from 'devextreme-angular';

@Component({
  selector: 'app-past-workshop-card',
  templateUrl: './past-workshop-card.component.html',
  styleUrls: ['./past-workshop-card.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    NgIconComponent,
    SharedWorkshopCardComponent,
    TranslateModule,
    DxPopupModule,
  ],
})
export class PastWorkshopCardComponent implements OnInit {
  @Input() workshop!: PastWorkshopModel;

  showContentsDialog: boolean = false;
  selectedContents: any[] = [];

  cardActions: SharedWorkshopCardAction[] = [];
  cardRows: SharedWorkshopCardRow[] = [];

  constructor(
    private profileService: ProfileService,
    private notifierService: NotifierService,
    private persianTitlesService: PersianTitlesService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initRows();
    this.initActions();
  }

  openContentsDialog(workshop: PastWorkshopModel): void {
    if (this.isContentsDownloadAllowed(workshop)) {
      this.selectedContents = workshop.contents;
      this.showContentsDialog = true;
    } else {
      this.notifierService.notify(
        'error',
        this.persianTitlesService.Phrases
          .MustCompleteSurveyToReceiveWorkshopContents,
      );
    }
  }

  downloadContent(content: any): void {
    this.profileService.downloadContent(content).subscribe(
      (blob) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = content.file_name;
        link.click();
      },
      () => {
        this.notifierService.notify('error', 'خطا در دانلود محتوا');
      },
    );
  }

  markContentsDownloadAsAllowed(workshop: PastWorkshopModel) {
    let visitedPastWorkshopSurveys: number[] =
      JSON.parse(localStorage.getItem('visitedPastWorkshopSurveys')) || [];
    visitedPastWorkshopSurveys.push(workshop.id);
    localStorage.setItem(
      'visitedPastWorkshopSurveys',
      JSON.stringify(visitedPastWorkshopSurveys),
    );
  }

  isContentsDownloadAllowed(workshop: PastWorkshopModel): boolean {
    if (!workshop.surveyLink || !workshop.holdingTimes.at(-1)) {
      return true;
    }
    const lastHoldingTimeEndDatetime = new Date(
      workshop.holdingTimes.at(-1)!.endDatetime,
    );
    const now = new Date();
    const timeGapInHours = Math.floor(
      (now.getTime() - lastHoldingTimeEndDatetime.getTime()) / 3600000,
    );
    if (timeGapInHours >= 24) {
      return true;
    }
    let visitedPastWorkshopSurveys: number[] =
      JSON.parse(localStorage.getItem('visitedPastWorkshopSurveys')) || [];
    return visitedPastWorkshopSurveys.includes(workshop.id);
  }

  concatTimes(holdingTimes: WorkshopHoldingTimeModel[]) {
    return holdingTimes?.map((e) => e.jalaliString).join(' | ');
  }

  formatMinutesToTime(total_minutes: number) {
    const hours = Math.floor(total_minutes / 60);
    const minutes = total_minutes % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  }

  private initRows() {
    this.cardRows = [
      {
        icon: 'lucideUser',
        title: this.translateService.instant('trainingHist.labels.teacher'),
        value: this.workshop.teacherFullName,
      },
      {
        icon: 'lucideCalendar',
        title: this.translateService.instant('trainingHist.labels.eventDate'),
        value:
          this.concatTimes(this.workshop.holdingTimes) +
          ' (' +
          this.formatMinutesToTime(this.workshop.duration) +
          ' ساعت)',
      },
      {
        icon: 'lucideLayers',
        title: this.translateService.instant('trainingHist.labels.method'),
        value: this.workshop.holdingTypeTitle,
      },
    ];

    if (this.workshop.locationTitle?.length) {
      this.cardRows.push({
        icon: 'lucideMapPin',
        title: this.translateService.instant('trainingHist.labels.location'),
        value: this.workshop.locationTitle,
      });
    }

    if (this.workshop.isPresent !== undefined) {
      const badge = this.workshop.isPresent
        ? this.translateService.instant('trainingHist.labels.present')
        : this.translateService.instant('trainingHist.labels.absent');
      this.cardRows.push({
        icon: '',
        title: '',
        value: '',
        badge: badge,
        badgeColor: this.workshop.isPresent ? 'success' : 'danger',
      });
    }
  }

  private initActions() {
    this.cardActions = [
      {
        type: 'button',
        label: this.translateService.instant('دریافت فایل‌ها'),
        class: 'btn btn-outline-success my-1',
        onClick: () => this.openContentsDialog(this.workshop),
        disabled: this.workshop.contents.length === 0,
      },
      ...(this.workshop.surveyLink
        ? [
            {
              type: 'link',
              label: this.translateService.instant('نظرسنجی'),
              class: 'text-decoration-none text-success',
              link: this.workshop.surveyLink,
              onClick: () => this.markContentsDownloadAsAllowed(this.workshop),
            },
          ]
        : []),
    ] as SharedWorkshopCardAction[];
  }
}
